/**
 * Program Unit Views — service layer for the Building Information Model tool's elevation/section
 * callouts, scoped to a Concept Unit. Backed by api/program-plans.js (Supabase
 * `program_unit_views` + the public `program-views` bucket). See docs/PROGRAM_MODEL.md —
 * "Unit Views." Views are loaded in bulk via `services/programPlans.js#getProgramModel` — this
 * file only covers create/update/feed/delete.
 *
 * Internal team tool only (Clerk-authed everywhere; no public/token path in v1).
 */

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/program-plans' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Program view request failed (' + res.status + ')'))
  return data
}

async function putSigned(signedUrl, body, contentType) {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'x-upsert': 'true' },
    body,
  })
  if (!res.ok) throw new Error('Upload failed (' + res.status + ')')
}

/**
 * Create a new elevation or section view. `callout` is anchor-relative FEET offsets (never raw
 * page fractions — see docs/PROGRAM_MODEL.md): `{ dx, dy, dirDx, dirDy }` for elevation,
 * `{ x1, y1, x2, y2, sideDx, sideDy }` for section.
 */
export async function createUnitView({ unitId, conceptId, projectId, viewType, name, originLevelId, callout }) {
  const { view } = await call('POST', { action: 'createView', unitId, conceptId, projectId, viewType, name, originLevelId, callout })
  return view
}

export async function updateViewCallout(viewId, callout) {
  const { view } = await call('POST', { action: 'updateViewCallout', viewId, callout })
  return view
}

export async function renameView(viewId, name) {
  const { view } = await call('POST', { action: 'renameView', viewId, name })
  return view
}

/** Commit a drag-handle reorder — rewrites sort_order sequentially in the given order. Called
 *  once on drop; the drag itself reorders local state optimistically (see ViewsList). */
export function reorderUnitViews(orderedViewIds) {
  return call('POST', { action: 'reorderViews', orderedViewIds })
}

/** Link a view to a saved /sketch document — the "draw it" feed path. */
export async function setViewSketch(viewId, sketchId) {
  const { view } = await call('POST', { action: 'setViewSketch', viewId, sketchId })
  return view
}

/** Start an upload (image or single PDF page) — the "upload it" feed path. */
export function createViewUploadUrl(viewId) {
  return call('POST', { action: 'viewUploadUrl', viewId }).then(r => r.viewUpload)
}

export function uploadViewFile(viewUpload, blob, contentType) {
  return putSigned(viewUpload.signedUrl, blob, contentType)
}

export async function finalizeViewUpload({ viewId, uploadPath, uploadThumbPath }) {
  const { view } = await call('POST', { action: 'finalizeViewUpload', viewId, uploadPath, uploadThumbPath })
  return view
}

export function deleteUnitView(viewId) {
  return call('POST', { action: 'deleteView', viewId })
}
